import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import HomeIcon from '@mui/icons-material/Home'
import InfoIcon from '@mui/icons-material/Info'
import BackIcon from '@mui/icons-material/NavigateBefore'
import NextIcon from '@mui/icons-material/NavigateNext'
import StartIcon from '@mui/icons-material/Start'
import EditIcon from '@mui/icons-material/Edit'

import { BottomNavigation, BottomNavigationAction, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TLung } from '../../data/Procedure'
import { TTimelineStep, isAssessmentStep } from '../../data/WorkflowSteps'
import NL from '../../utils/NavigationLabel'
import ChangeProcedureDialog from './ChangeProcedureDialog'
import EndProcedureConfirm from './EndProcedureConfirm'
import { useAppDispatch, useAppSelector } from './RunReduxHooks'
import UIInfo from './UIInfo'
import { getBypassWaitingForNext } from './allTime'
import { LungBothIcon, LungLeftIcon, LungRightIcon } from './icons'
import { advance, finishProcedureAsync, finishProcedureOfflineAsync, goBack, selectCanAdvance, selectCanGoBack, selectCanStartProcedure, selectProcHeader, selectStep, startProcedure } from './sliceRunProcHeader'
import { selectData, setflag } from './sliceTimeline'
import React from 'react'
import CytoEditDialog from './CytoEditDialog'
import { clearStorage } from './RunStore'
import { IBasicEntityBase } from '../../data/Common'

async function finishProcedureWithLocalFallback(dispatch: ReturnType<typeof useAppDispatch>, identity: IBasicEntityBase) {
    const result = await dispatch(finishProcedureAsync(identity))

    if (!finishProcedureAsync.fulfilled.match(result) || result.payload === null) {
        await dispatch(finishProcedureOfflineAsync(identity))
    }
}

React.memo(BMSetup)
export function BMSetup({ setCanPlayAudio }: { setCanPlayAudio: (b: boolean) => void }) {
    const canAdvance = useAppSelector(selectCanAdvance)
    const canStartProcedure = useAppSelector(selectCanStartProcedure)
    const canGoBack = useAppSelector(selectCanGoBack)
    const dispatch = useAppDispatch()
    const currentWorkflowStep = useAppSelector(selectStep)
    const [infoOpen, setInfoOpen] = useState(false)

    const infoSX = currentWorkflowStep === 'Procedure'
        ? { visibility: 'hidden' }
        : {}

    const infoIcon = <InfoIcon />

    const infoLabel = NL('Info')

    const nextClicked = () => {
        if (canStartProcedure) {
            dispatch(startProcedure())
            setCanPlayAudio(true)
        } else if (canAdvance) {
            dispatch(advance())
        }
    }

    const nextLabel = canStartProcedure
        ? NL('Start Perfusion')
        : canAdvance
            ? NL('Next')
            : null

    const nextIcon = canStartProcedure
        ? <StartIcon />
        : canAdvance
            ? <NextIcon />
            : null

    return (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
            <BottomNavigation sx={{ verticalAlign: 'center' }} showLabels>
                <BottomNavigationAction sx={!canGoBack ? { visibility: 'hidden' } : {}} label={NL('Back')} icon={<BackIcon />} onClick={() => dispatch(goBack())} />
                <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                <BottomNavigationAction sx={infoSX} label={infoLabel} icon={infoIcon} onClick={() => { setInfoOpen(true) }} />
                <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                <BottomNavigationAction
                    sx={!(canAdvance || canStartProcedure) ? { visibility: 'hidden' } : {}}
                    label={nextLabel}
                    icon={nextIcon}
                    onClick={nextClicked}
                />
            </BottomNavigation>
            <UIInfo show={infoOpen} tabbedView={false} closeCallback={() => setInfoOpen(false)} key={Math.random()} />
        </Paper>
    )
}

function RenderLungIcon({ lung }: { lung: TLung }) {
    switch (lung) {
        case 'right': {
            return <LungRightIcon />
        }
        case 'left': {
            return <LungLeftIcon />
        }
        case 'both': {
            return <LungBothIcon />
        }
    }

    return null
}

React.memo(BMRampup)
export function BMRampup() {
    const [openConfirmProcedureEndDialog, setOpenConfirmProcedureEndDialog] = useState(false)
    const currentWorkflowStep = useAppSelector(selectStep)
    const dispatch = useAppDispatch()
    const header = useAppSelector(selectProcHeader)
    const lung = header!.lung
    const [changeProcedureOpen, setChangeProcedureOpen] = useState(false)
    const [infoOpen, setInfoOpen] = useState(false)

    const nextClicked = () => {
        dispatch(advance())
    }

    const confirmCallback = (isConfirmed: boolean) => {
        setOpenConfirmProcedureEndDialog(false)

        if (isConfirmed) {
            finishProcedureWithLocalFallback(dispatch, { partitionKey: header!.partitionKey, rowKey: header!.rowKey })
        }
    }

    const testAdvanceSX = currentWorkflowStep === '0:20'
        ? { visibility: 'hidden' }
        : {}

    const advanceSX = currentWorkflowStep === '0:20'
        ? {}
        : { visibility: 'hidden' }

    return (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
            <BottomNavigation sx={{ verticalAlign: 'center' }} showLabels>
                <BottomNavigationAction icon={<RenderLungIcon lung={lung} />} label={NL(lung)} onClick={() => setChangeProcedureOpen(true)} />
                {getBypassWaitingForNext()
                    ? <BottomNavigationAction sx={testAdvanceSX} label={NL('Next')} icon={<NextIcon />} onClick={() => dispatch(advance())} />
                    : <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                }
                <BottomNavigationAction label={NL('Info')} icon={<InfoIcon />} onClick={() => setInfoOpen(true)} />
                <BottomNavigationAction sx={advanceSX} label={NL('Continue (32°C)')} icon={<StartIcon />} onClick={nextClicked} />
                <BottomNavigationAction
                    label={NL('Stop procedure')}
                    icon={<HighlightOffIcon sx={{ color: 'red' }} />}
                    onClick={() => setOpenConfirmProcedureEndDialog(true)}
                />
            </BottomNavigation>
            <ChangeProcedureDialog key={Math.random()} open={changeProcedureOpen} closeCallback={() => setChangeProcedureOpen(false)} />
            {openConfirmProcedureEndDialog
                ? <EndProcedureConfirm key={Math.random()} confirmCallBack={confirmCallback} />
                : null}
            <UIInfo show={infoOpen} tabbedView={false} closeCallback={() => setInfoOpen(false)} key={Math.random()} />
        </Paper>
    )
}

React.memo(BMSteady)
export function BMSteady() {
    const [openConfirmProcedureEndDialog, setOpenConfirmProcedureEndDialog] = useState(false)
    const [openCytoEditDialog, setOpenCytoEditDialog] = useState(false)
    const currentWorkflowStep = useAppSelector(selectStep)!
    const isMandatoryStepDataEntered = useAppSelector(selectData)[currentWorkflowStep as TTimelineStep].IsMandatoryDataEntered ?? false
    const isStepDataEntryFinished = useAppSelector(selectData)[currentWorkflowStep as TTimelineStep].IsDataEntryFinished ?? false
    const dispatch = useAppDispatch()
    const header = useAppSelector(selectProcHeader)
    const lung = header!.lung
    const [changeProcedureOpen, setChangeProcedureOpen] = useState(false)
    const [infoOpen, setInfoOpen] = useState(false)

    let continueSX: any = { visibility: 'hidden' }

    if (isAssessmentStep(currentWorkflowStep)) {
        if (!isMandatoryStepDataEntered && !isStepDataEntryFinished) {
        } else if (!isMandatoryStepDataEntered && isStepDataEntryFinished) {
        } else if (isMandatoryStepDataEntered && !isStepDataEntryFinished) {
            continueSX = {}
        } else if (isMandatoryStepDataEntered && isStepDataEntryFinished) {
        }
    } else {
        continueSX = isMandatoryStepDataEntered && !isStepDataEntryFinished
            ? {}
            : { visibility: 'hidden' }
    }

    const nextClicked = () => {
        dispatch(advance())
    }

    const continueClicked = () => {
        dispatch(setflag({ fieldName: 'IsDataEntryFinished', value: true, step: currentWorkflowStep as TTimelineStep }))
    }

    const confirmCallback = (isConfirmed: boolean) => {
        setOpenConfirmProcedureEndDialog(false)

        if (isConfirmed) {
            finishProcedureWithLocalFallback(dispatch, { partitionKey: header!.partitionKey, rowKey: header!.rowKey })
        }
    }

    return (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
            <BottomNavigation sx={{ verticalAlign: 'center' }} showLabels>
                <BottomNavigationAction icon={<RenderLungIcon lung={lung} />} label={NL(lung)} onClick={() => setChangeProcedureOpen(true)} />
                <BottomNavigationAction sx={{ visibility: 'hidden' }} label="Next" icon={<NextIcon />} onClick={nextClicked} />
                <BottomNavigationAction label={NL('Info/Data')} icon={<InfoIcon />} onClick={() => { setInfoOpen(true) }} />
                <BottomNavigationAction sx={continueSX} label="Continue" icon={<StartIcon />} onClick={continueClicked} />
                <BottomNavigationAction label={NL('Edit Cytokines')} icon={<EditIcon />} onClick={() => setOpenCytoEditDialog(true)} />
                <BottomNavigationAction label={NL('Stop procedure')} icon={<HighlightOffIcon sx={{ color: 'red' }} />} onClick={() => setOpenConfirmProcedureEndDialog(true)} />
            </BottomNavigation>
            <ChangeProcedureDialog key={Math.random()} open={changeProcedureOpen} closeCallback={() => setChangeProcedureOpen(false)} />
            {openConfirmProcedureEndDialog
                ? <EndProcedureConfirm key={Math.random()} confirmCallBack={confirmCallback} />
                : null
            }
            <UIInfo show={infoOpen} tabbedView={true} closeCallback={() => setInfoOpen(false)} key={Math.random()} />
            <CytoEditDialog show={openCytoEditDialog} closeCallback={() => setOpenCytoEditDialog(false)} key={Math.random()} />
        </Paper>
    )
}

React.memo(BMFinished)
export function BMFinished({ isNextAvailable }: { isNextAvailable: boolean }) {
    const [infoOpen, setInfoOpen] = useState(false)
    const [homeDialogOpen, setHomeDialogOpen] = useState(false)
    const [homeDialogText, setHomeDialogText] = useState('')
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const header = useAppSelector(selectProcHeader)

    const handleCloseHomeDialog = () => {
        setHomeDialogOpen(false)
    }

    const handleHomeClick = async () => {
        if (header?.partitionKey && header?.rowKey) {
            const result = await dispatch(finishProcedureAsync({
                partitionKey: header.partitionKey,
                rowKey: header.rowKey
            }))

            if (!finishProcedureAsync.fulfilled.match(result) || result.payload === null) {
                setHomeDialogText(`Internet connection lost or you were signed out from your account.
Do not refresh this page or close this window, it may result in a loss of procedure data.
Keep window open until internet connection is established or open a separate tab and sign-in before returning to this window.`)
                setHomeDialogOpen(true)
                return
            }
        }

        await clearStorage()
        navigate('/tech/current', { replace: true })
    }

    return (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
            <BottomNavigation sx={{ verticalAlign: 'center' }} showLabels>
                <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                <BottomNavigationAction
                    label={NL('Info/Data')}
                    icon={<InfoIcon />}
                    onClick={() => {
                        setInfoOpen(true)
                    }}
                />
                <BottomNavigationAction sx={{ visibility: 'hidden' }} />
                <BottomNavigationAction
                    sx={isNextAvailable ? {} : { visibility: 'hidden' }}
                    label={NL('Home')}
                    icon={<HomeIcon />}
                    onClick={handleHomeClick}
                />
            </BottomNavigation>

            <UIInfo show={infoOpen} tabbedView={true} closeCallback={() => setInfoOpen(false)} key={Math.random()} />

            <Dialog open={homeDialogOpen} onClose={handleCloseHomeDialog}>
                <DialogTitle>WARNING - DATA MAY NOT HAVE BEEN SAVED</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-warning" style={{ fontWeight: 'bold', whiteSpace: 'pre-line' }}>
                        {homeDialogText !== ''
                            ? homeDialogText
                            : `Internet connection lost or you were signed out from your account.
Do not refresh this page or close this window, it may result in a loss of procedure data.
Keep window open until internet connection is established or open a separate tab and sign-in before returning to this window.`
                        }
                    </DialogContentText>
                    <br />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHomeDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}